/**
 * Woodtastic card add-to-cart (optimistic UI + cart drawer — "Option C").
 *
 * On click of "In den Einkaufswagen":
 *  1) Immediately swap the button with an optimistic stepper showing "1 im Korb"
 *  2) Fire AJAX cart/add.js in the background
 *  3) When it returns, replace with the server-rendered stepper (authoritative)
 *  4) Open the cart drawer as confirmation (slide-in), so the customer can
 *     review, continue shopping, or check out. The grid stays behind it.
 *  5) If AJAX fails, roll back to the button
 *
 * The drawer is opened through Dawn's own cart-drawer.renderContents(), the
 * exact same path the product page add-to-cart uses — keeping behavior
 * consistent everywhere (home, collections, product recommendations).
 */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    const btn = event.target.closest('.woodtastic-card-add-btn');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    // Prevent double-processing same click
    if (btn.dataset.wtInFlight === '1') return;
    btn.dataset.wtInFlight = '1';

    const variantId = btn.dataset.variantId;
    const sectionId = btn.dataset.sectionId;
    const bulkContainer = btn.closest('quick-add-bulk');
    if (!variantId || !bulkContainer) return;

    // Pull localized strings from the Liquid-rendered button. Fallbacks keep
    // existing behavior if any data-attr is missing (e.g. older cached markup).
    const i18n = {
      decrease: btn.dataset.i18nDecrease || 'Decrease',
      increase: btn.dataset.i18nIncrease || 'Increase',
      inputLabel: btn.dataset.i18nInputLabel || 'Quantity',
      cartSuffix: btn.dataset.i18nCartSuffix || 'in cart',
    };

    // Snapshot for rollback on error
    const originalHtml = bulkContainer.innerHTML;

    // 1) OPTIMISTIC: swap to a stepper at qty=1 IMMEDIATELY (no wait).
    bulkContainer.innerHTML = buildOptimisticStepper(variantId, i18n);

    // 2) Fire AJAX in background.
    // Ask cart/add.js to render every section we need in the SAME response
    // (the card's bulk section, the header bubble, AND the cart drawer).
    // This replaces a separate ?section_id=cart-drawer round-trip — saves
    // 100–300 ms on slow mobile networks.
    const sectionsToFetch = [sectionId, 'cart-icon-bubble', 'cart-drawer'];
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sectionsToFetch.join(','));
    formData.append('sections_url', window.location.pathname);

    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('Add to cart failed:', response.description || response.message);
          bulkContainer.innerHTML = originalHtml;
          return;
        }

        // 3) Replace with the server-rendered authoritative stepper
        const sectionHtml = response.sections && response.sections[sectionId];
        if (sectionHtml) {
          const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const newBulk = parsed.querySelector(`#quick-add-bulk-${variantId}-${sectionId}`);
          if (newBulk) {
            bulkContainer.innerHTML = newBulk.innerHTML;
          }
        }

        // Update cart icon bubble
        const cartBubbleHtml = response.sections && response.sections['cart-icon-bubble'];
        if (cartBubbleHtml) {
          const bubbleEl = document.getElementById('cart-icon-bubble');
          if (bubbleEl) {
            const parsed = new DOMParser().parseFromString(cartBubbleHtml, 'text/html');
            const newBubble = parsed.querySelector('.shopify-section');
            if (newBubble) {
              bubbleEl.innerHTML = newBubble.innerHTML;
            }
          }
        }

        // Notify any listeners that the cart changed (count badges, etc.)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'woodtastic-card-add',
            cartData: response,
            variantId: parseInt(variantId),
          });
        }

        // C: open the cart drawer as confirmation (slide-in), reusing Dawn's own
        // renderContents() — the SAME path the product page uses. It updates
        // #CartDrawer + cart-icon-bubble from THIS response and opens the drawer
        // with the overlay-close handler wired. The grid stays behind; the
        // drawer's "Weiter einkaufen" / checkout give the next step.
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          if (cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }
          cartDrawer.renderContents(response);
        }
      })
      .catch((err) => {
        console.error('Woodtastic add-to-cart error:', err);
        bulkContainer.innerHTML = originalHtml;
      });
  });

  /**
   * Minimal stepper HTML that mirrors the real Dawn stepper layout so the
   * optimistic swap is visually identical. Server response overwrites this
   * a moment later with full Dawn markup (including progress bar, data-index etc).
   * Plus button is disabled initially; becomes active when real markup arrives.
   */
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function escapeText(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  }

  function buildOptimisticStepper(variantId, i18n) {
    return [
      '<quantity-input class="quantity cart-quantity quantity--with-label">',
      '<button class="quantity__button" name="minus" type="button" disabled aria-label="' + escapeAttr(i18n.decrease) + '">',
      '<span class="svg-wrapper">',
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-minus" viewBox="0 0 10 2">',
      '<path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 0 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"/>',
      '</svg>',
      '</span>',
      '</button>',
      '<div class="quantity__middle">',
      // size="1" prevents browsers from giving the number input its default
      // size=20 character width — which would push the suffix to the right.
      '<input class="quantity__input" type="number" value="1" min="0" size="1" data-index="' + variantId + '" aria-label="' + escapeAttr(i18n.inputLabel) + '" readonly>',
      '<span class="quantity__suffix" aria-hidden="true">' + escapeText(i18n.cartSuffix) + '</span>',
      '</div>',
      '<button class="quantity__button" name="plus" type="button" disabled aria-label="' + escapeAttr(i18n.increase) + '">',
      '<span class="svg-wrapper">',
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10">',
      '<path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/>',
      '</svg>',
      '</span>',
      '</button>',
      '</quantity-input>',
    ].join('');
  }

})();
